import React, { useState, useEffect } from 'react';
// import { toast } from 'react-hot-toast';
import { User, Building, Calendar, DollarSign, Target, Upload, Music, Mail, Phone, MapPin, Loader, CreditCard, AlertCircle, BadgeCheck, Download, Search, X, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toE164 } from '../lib/phone';
import { normalizeSpotifyUri } from '../lib/releaseAssets';

interface CampaignSubmissionData {
  // Step 1: User type
  user_type: 'artist' | 'manager' | '';
  
  // Step 1.5: Customer status
  customer_status: string; // 'new' | 'existing' | ''
  
  // Step 1.7: Verification (for existing customers)
  verification_method: 'phone' | 'email' | '';
  verification_phone: string;
  verification_email: string;
  verification_code: string; // User input
  generated_code: string; // Internal storage for verification
  is_phone_verified: boolean;
  selected_artist_id: string; // ID of selected artist from verification

  // Artist/Contact data
  kuenstlername: string;
  vorname: string;
  nachname: string;
  firma: string;
  email: string;
  telefon: string;
  
  // Artist address
  strasse: string;
  plz: string;
  ort: string;
  land: string;
  vat_id: string;
  
  // Billing address (if different)
  billing_same_as_artist: boolean;
  billing_vorname: string;
  billing_nachname: string;
  billing_firma: string;
  billing_strasse: string;
  billing_plz: string;
  billing_ort: string;
  billing_land: string;
  billing_email: string;
  billing_telefon: string;
  
  // Release information
  release_published: string; // 'yes' | 'no' | ''
  spotify_track_url: string; // For published releases
  release_name: string;
  release_date: string;
  master_datei_link: string;
  master_datei_file?: File;
  cover_link: string;
  cover_file?: File;
  spotify_uri: string;
  
  // Artist Socials (Step 5)
  instagram_url: string;
  tiktok_url: string;
  facebook_page_url: string;
  
  content_ordner: string;
  
  // Budget & services
  werbebudget_netto: number;
  campaign_management: boolean;
  content_strategy_upsell: boolean;
  voucher_promocode: string;
  
  // Final confirmation
  final_confirmation: boolean;
}

const NewCampaignSubmission: React.FC = () => {
  const { currentUser } = useAuth();

  // Calculate next upcoming Friday
  const getNextFriday = (): string => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 5 = Friday, 6 = Saturday
    const daysUntilFriday = (5 - dayOfWeek + 7) % 7 || 7; // Days until next Friday (0 if today is Friday, so use 7)
    const nextFriday = new Date(today);
    nextFriday.setDate(today.getDate() + daysUntilFriday);
    return nextFriday.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  const [formData, setFormData] = useState<CampaignSubmissionData>({
    // Step 1: User type
    user_type: '',
    
    // Step 1.5: Customer status
    customer_status: '',
    
    // Step 1.7: Verification (for existing customers)
    verification_method: '',
    verification_phone: '',
    verification_email: '',
    verification_code: '',
    generated_code: '',
    is_phone_verified: false,
    selected_artist_id: '',

    // Artist/Contact data
    kuenstlername: '',
    vorname: '',
    nachname: '',
    firma: '',
    email: '',
    telefon: '',
    
    // Artist address
    strasse: '',
    plz: '',
    ort: '',
    land: 'Germany',
    vat_id: '',
    
    // Billing address (if different)
    billing_same_as_artist: true,
    billing_vorname: '',
    billing_nachname: '',
    billing_firma: '',
    billing_strasse: '',
    billing_plz: '',
    billing_ort: '',
    billing_land: 'Germany',
    billing_email: '',
    billing_telefon: '',
    
    // Release information
    release_published: '',
    spotify_track_url: '',
    release_name: '',
    release_date: getNextFriday(),
    master_datei_link: '',
    cover_link: '',
    spotify_uri: '',
    
    // Artist Socials
    instagram_url: '',
    tiktok_url: '',
    facebook_page_url: '',
    
    content_ordner: '',
    
    // Budget & services
    werbebudget_netto: 650,
    campaign_management: true,
    content_strategy_upsell: false,
    voucher_promocode: '',
    
    // Final confirmation
    final_confirmation: false,
  });

  const DRAFT_KEY = 'campaign_draft';
  const [draftRestored, setDraftRestored] = useState(false);

  // ─── Load draft from localStorage on mount ──────────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        console.log('💾 Restoring campaign draft from localStorage');
        // Don't restore File objects (can't be serialized) or verification code
        setFormData(prev => ({
          ...prev,
          ...draft,
          // Don't restore these fields — they're transient or sensitive
          master_datei_file: undefined,
          cover_file: undefined,
          generated_code: '',
          is_phone_verified: false, // Re-verify on reload
          final_confirmation: false, // Don't auto-confirm
        }));
      }
    } catch (error) {
      console.error('Error loading campaign draft:', error);
    }
    setDraftRestored(true);
  }, []);

  // ─── Save draft to localStorage on form data change ─────────────────────
  useEffect(() => {
    if (!draftRestored) return; // Don't save before we've loaded the draft
    try {
      // Strip File objects and sensitive fields before saving
      const { master_datei_file, cover_file, generated_code, ...savable } = formData;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(savable));
    } catch (error) {
      console.error('Error saving campaign draft:', error);
    }
  }, [formData, draftRestored]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingArtists, setIsLoadingArtists] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [isSendingWhatsAppCode, setIsSendingWhatsAppCode] = useState(false);
  const [associatedArtists, setAssociatedArtists] = useState<any[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6;
  const [fetchingSpotifyTrack, setFetchingSpotifyTrack] = useState(false);
  const [spotifyTrackData, setSpotifyTrackData] = useState<any>(null);
  const [showConfirmationWarning, setShowConfirmationWarning] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState<any>(null);
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [artistsLoaded, setArtistsLoaded] = useState(false);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [generatedEmailCode, setGeneratedEmailCode] = useState('');
  const [pendingEmailArtists, setPendingEmailArtists] = useState<any[]>([]);
  const [artistLookupComplete, setArtistLookupComplete] = useState(false);
  const [customAgencyFee, setCustomAgencyFee] = useState<number | null>(null);
  const [leftoverBudget, setLeftoverBudget] = useState<number | null>(null);
  const [leftoverBudgetLoading, setLeftoverBudgetLoading] = useState(false);
  const [feeDiscountEligible, setFeeDiscountEligible] = useState(false);

  // Step 2 phone verification state (for new customers who skipped step 1.7)
  const [step2CodeSent, setStep2CodeSent] = useState(false);
  const [step2CodeInput, setStep2CodeInput] = useState('');
  const [step2Verifying, setStep2Verifying] = useState(false);
  const [step2PhoneVerified, setStep2PhoneVerified] = useState(false);
  const [step2GeneratedCode, setStep2GeneratedCode] = useState('');

  // Monday.com import state
  const [showMondayImport, setShowMondayImport] = useState(false);
  const [mondayItems, setMondayItems] = useState<any[]>([]);
  const [mondayLoading, setMondayLoading] = useState(false);
  const [mondayLoadingMore, setMondayLoadingMore] = useState(false);
  const [mondaySearch, setMondaySearch] = useState('');
  const [mondayHasMore, setMondayHasMore] = useState(false);
  const [mondayOffset, setMondayOffset] = useState(0);

  // Read URL parameter for custom agency fee
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const typeParam = urlParams.get('type');
    if (typeParam) {
      const fee = parseInt(typeParam, 10);
      if (!isNaN(fee)) {
        setCustomAgencyFee(fee);
      }
    }
  }, []);

  // Load all artists for authenticated users, or cached verification for public users
  // Only run once on mount
  useEffect(() => {
    if (artistsLoaded) return; // Prevent re-loading
    
    if (currentUser) {
      // Authenticated user - load all artists from database
      console.log('🔐 Authenticated user detected, loading all artists');
      loadAllArtists();
      setArtistsLoaded(true);
    } else {
      // Public user - check cache
      const cachedVerification = sessionStorage.getItem('phone_verification');
      console.log('🔍 Checking cache:', cachedVerification);
      
      if (cachedVerification) {
        try {
          const cachedData = JSON.parse(cachedVerification);
          // Only restore if verification is complete
          if (cachedData.verified && cachedData.verified === true) {
            console.log('✅ Found cached phone verification, restoring state');
            setAssociatedArtists(cachedData.artists || []);
            setArtistLookupComplete(true);
            setFormData(prev => ({
              ...prev,
              verification_phone: cachedData.phone,
              is_phone_verified: true
            }));

            if (!cachedData.artists || cachedData.artists.length === 0) {
              const phone = cachedData.phone;
              console.log('⚠️ No artists in cache, fetching from database...');
              // If no artists in cache, fetch them
              lookupArtistsByWhatsAppGroups(phone);
            }

            console.log('✅ Restored verification from cache:', cachedData.phone);
          } else {
            // Cache has incomplete data, clear it
            console.log('⚠️ Cache has incomplete verification, clearing');
            sessionStorage.removeItem('phone_verification');
          }
        } catch (error) {
          console.error('Error loading cached verification:', error);
          sessionStorage.removeItem('phone_verification');
        }
      }
      setArtistsLoaded(true);
    }
  }, [currentUser, artistsLoaded]);

  const sendWhatsAppVerification = async () => {
    if (!formData.verification_phone) {
      // toast.error('Bitte gib eine Telefonnummer ein');
      return;
    }

    setIsSendingWhatsAppCode(true);
    try {
      // Only generate a new code if one hasn't been sent yet
      let verificationCode = formData.generated_code;
      if (!verificationCode) {
        verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
        setFormData((prev: CampaignSubmissionData) => ({ ...prev, generated_code: verificationCode }));
      }
      
      const response = await fetch('https://wrlgoxbzlngdtomjhvnz.supabase.co/functions/v1/send-whatsapp-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          phone: toE164(formData.verification_phone) ?? formData.verification_phone,
          code: verificationCode,
          name: 'Kunde' // Generic name for campaign form
        })
      });

      if (!response.ok) {
        throw new Error('Fehler beim Senden des Codes');
      }

      await response.json();
      setCodeSent(true);
      // toast.success('Verifikationscode wurde per WhatsApp gesendet!');
    } catch (error) {
      console.error('Error sending WhatsApp verification:', error);
      // toast.error('Fehler beim Senden des WhatsApp-Codes. Bitte versuche es erneut.');
    } finally {
      setIsSendingWhatsAppCode(false);
    }
  };

  const verifyCode = async (code?: string) => {
    const codeToVerify = code || formData.verification_code;
    console.log('🔐 verifyCode called');
    console.log('🔐 Verification code:', codeToVerify);
    console.log('🔐 Generated code:', formData.generated_code);

    if (!codeToVerify || codeToVerify.length !== 6) {
      // toast.error('Bitte gib einen 6-stelligen Code ein');
      return;
    }

    if (codeToVerify !== formData.generated_code) {
      // toast.error('Ungültiger Code. Bitte versuche es erneut.');
      return;
    }

    try {
      console.log('🔐 Marking phone as verified...');

      // Update state synchronously
      setFormData((prev: CampaignSubmissionData) => {
        console.log('🔐 Updating formData - is_phone_verified: true');
        return { ...prev, is_phone_verified: true };
      });

      // Wait a bit for state to propagate
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('🔐 Looking up artists via WhatsApp groups...');
      // Look up artists via WhatsApp group membership
      await lookupArtistsByWhatsAppGroups(formData.verification_phone);
      
      console.log('🔐 Verification complete!');
      
      // Cache verification in sessionStorage
      sessionStorage.setItem('phone_verification', JSON.stringify({
        phone: formData.verification_phone,
        verified: true,
        artists: associatedArtists,
        timestamp: new Date().toISOString()
      }));
      
      // toast.success('Code erfolgreich verifiziert!');
    } catch (error) {
      console.error('Error verifying code:', error);
      // toast.error('Fehler bei der Verifizierung');
    }
  };

  const loadAllArtists = async () => {
    setIsLoadingArtists(true);
    try {
      console.log('🎨 Loading all artists from database for authenticated user');
      
      const { data: artists, error } = await supabase
        .from('artists')
        .select('id, name, instagram_url, tiktok_url, spotify_url, archived')
        .order('name', { ascending: true });

      if (error) throw error;

      // Filter out archived artists
      const activeArtists = (artists || []).filter((artist: any) => !artist.archived);
      
      console.log('✅ Loaded artists:', activeArtists.length);
      setAssociatedArtists(activeArtists);
      setArtistLookupComplete(true);
      
      // Mark as verified to skip phone verification step
      setFormData(prev => ({
        ...prev,
        is_phone_verified: true
      }));
    } catch (error) {
      console.error('Error loading all artists:', error);
      // toast.error('Fehler beim Laden der Künstler');
    } finally {
      setIsLoadingArtists(false);
    }
  };

  const lookupArtistsByWhatsAppGroups = async (phone: string) => {
    setIsLoadingArtists(true);
    try {
      const normalizedPhone = toE164(phone) ?? phone;
      console.log('🔍 Looking up artists via WhatsApp groups for phone:', phone, '→', normalizedPhone);
      
      // Call the edge function to check WhatsApp group membership
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/lookup-whatsapp-groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          phone: normalizedPhone,
          cache: false
        })
      });

      if (!response.ok) {
        throw new Error('Fehler beim Abrufen der WhatsApp-Gruppen');
      }

      const data = await response.json();
      console.log('🎯 WhatsApp group lookup response:', data);
      
      const artists = data.artists || [];
      
      console.log('🎯 Found artists via WhatsApp groups:', artists);
      console.log('🎯 Setting associated artists, length:', artists.length);
      setAssociatedArtists(artists);

      // Mark lookup as complete
      setArtistLookupComplete(true);

      if (artists.length === 0) {
        // No artists found - show option to create new artist
        setArtistLookupComplete(true);
        // toast.success(`Keine Künstler für Nummer ${formData.verification_phone} gefunden. Du kannst einen neuen Künstler anlegen.`);
      } else {
        // Artists found - show selection
        // toast.success(`${artists.length} Künstler für diese Nummer gefunden.`);
      }
    } catch (error) {
      console.error('Error looking up artists via WhatsApp groups:', error);
      // toast.error('Fehler beim Laden der Künstlerdaten');
    } finally {
      setIsLoadingArtists(false);
    }
  };

  const lookupArtistsByEmail = async (email: string) => {
    try {
      console.log('📧 Looking up artists by email:', email);

      // Find contact by email
      const { data: contacts, error: contactError } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, phone')
        .eq('email' as any, email.trim().toLowerCase() as any);

      if (contactError) throw contactError;

      if (!contacts || contacts.length === 0) {
        setAssociatedArtists([]);
        setFormData(prev => ({ ...prev, is_phone_verified: true }));
        // toast.success('Keine Künstler für diese E-Mail gefunden. Du kannst einen neuen Künstler anlegen.');
        return;
      }

      // Get artist IDs linked to these contacts
      const contactIds = contacts.map((c: any) => c.id);
      const { data: artistContacts, error: acError } = await supabase
        .from('artist_contacts')
        .select('artist_id')
        .in('contact_id', contactIds);

      if (acError) throw acError;

      if (!artistContacts || artistContacts.length === 0) {
        setAssociatedArtists([]);
        setFormData(prev => ({ ...prev, is_phone_verified: true }));
        // toast.success('Keine Künstler für diese E-Mail gefunden. Du kannst einen neuen Künstler anlegen.');
        return;
      }

      const artistIds = artistContacts.map((ac: any) => ac.artist_id);
      const { data: artists, error: artistError } = await supabase
        .from('artists')
        .select('id, name, instagram_url, tiktok_url, spotify_url, archived')
        .in('id', artistIds)
        .order('name');

      if (artistError) throw artistError;

      const activeArtists = (artists || []).filter((a: any) => !a.archived);
      console.log('✅ Found artists by email:', activeArtists.length);

      if (activeArtists.length === 0) {
        setAssociatedArtists([]);
        setFormData(prev => ({ ...prev, is_phone_verified: true }));
        // toast.success('Keine Künstler für diese E-Mail gefunden. Du kannst einen neuen Künstler anlegen.');
        return;
      }

      // Artists found — send verification code to email
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      setGeneratedEmailCode(code);
      setPendingEmailArtists(activeArtists);

      const response = await fetch('https://wrlgoxbzlngdtomjhvnz.supabase.co/functions/v1/send-email-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          code,
          name: (activeArtists[0] as any)?.name || 'Kunde'
        })
      });

      if (response.ok) {
        await response.json();
        console.log('✅ Email Verification: Code sent successfully');
        setEmailCodeSent(true);
        // toast.success(`Verifikationscode wurde an ${email} gesendet.`);
      } else {
        // Email sending failed — allow user to proceed anyway as fallback
        console.warn('⚠️ Email sending failed, allowing direct access as fallback');
        setAssociatedArtists(activeArtists);
        setFormData(prev => ({ ...prev, is_phone_verified: true }));
        setArtistLookupComplete(true);
        // toast('E-Mail konnte nicht gesendet werden. Künstler werden direkt angezeigt.');
      }
    } catch (error) {
      console.error('Error looking up artists by email:', error);
      // toast.error('Fehler beim Laden der Künstlerdaten');
    }
  };

  const verifyEmailCode = (code: string) => {
    if (code !== generatedEmailCode) {
      // toast.error('Ungültiger Code. Bitte versuche es erneut.');
      return;
    }
    // Code correct — reveal artists
    setAssociatedArtists(pendingEmailArtists);
    setFormData(prev => ({ ...prev, is_phone_verified: true }));
    setArtistLookupComplete(true);
    // toast.success(`${pendingEmailArtists.length} Künstler verifiziert.`);
  };

  // ─── Step 2 phone verification (for new customers) ──────────────────────
  // New customers who skipped step 1.7 verification must verify their phone
  // number via WhatsApp before proceeding to step 3. After successful
  // verification, a user account is created with a placeholder email.
  const sendStep2WhatsAppCode = async () => {
    if (!formData.telefon) return;

    setStep2Verifying(true);
    try {
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      setStep2GeneratedCode(code);

      const response = await fetch('https://wrlgoxbzlngdtomjhvnz.supabase.co/functions/v1/send-whatsapp-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          phone: toE164(formData.telefon) ?? formData.telefon,
          code: code,
          name: formData.kuenstlername || 'Kunde',
        }),
      });

      if (!response.ok) throw new Error('Failed to send code');

      setStep2CodeSent(true);
      console.log('✅ Step 2 WhatsApp code sent to:', formData.telefon);
    } catch (error) {
      console.error('Error sending step 2 WhatsApp code:', error);
    } finally {
      setStep2Verifying(false);
    }
  };

  const verifyStep2Code = async () => {
    if (step2CodeInput.length !== 6) return;
    if (step2CodeInput !== step2GeneratedCode) {
      console.warn('Invalid step 2 verification code');
      return;
    }

    setStep2Verifying(true);
    try {
      console.log('✅ Step 2 phone verified, creating user account...');

      // Create user account with placeholder email (best-effort — don't block on failure)
      try {
        const createResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-user-from-campaign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            phone: toE164(formData.telefon) ?? formData.telefon,
            name: `${formData.vorname} ${formData.nachname}`.trim(),
            artist_name: formData.kuenstlername,
            email: formData.email,
            vorname: formData.vorname,
            nachname: formData.nachname,
            firma: formData.firma,
            strasse: formData.strasse,
            plz: formData.plz,
            ort: formData.ort,
            land: formData.land,
            vat_id: formData.vat_id,
          }),
        });

        if (createResponse.ok) {
          const result = await createResponse.json();
          console.log('✅ User account created:', result.already_exists ? '(already exists)' : result.email);
        } else {
          console.warn('User account creation failed (non-blocking):', await createResponse.text());
        }
      } catch (createErr) {
        console.warn('User account creation error (non-blocking):', createErr);
      }

      setStep2PhoneVerified(true);
      setFormData(prev => ({ ...prev, is_phone_verified: true }));

      // Advance to step 3
      setTimeout(() => setCurrentStep(3), 100);
    } catch (error) {
      console.error('Error in step 2 verification:', error);
    } finally {
      setStep2Verifying(false);
    }
  };

  // ─── Leftover budget detection ─────────────────────────────────────────
  // When an existing artist is selected, check if they have completed projects
  // with linked reports where the actual spend was less than the budget.
  // If so, the leftover can be applied to the new campaign's ad budget.
  // Uses an edge function with the service role key (project_tracking and
  // campaign_metrics_cache are authenticated-only via RLS).
  const fetchLeftoverBudget = async (artistId: string) => {
    setLeftoverBudgetLoading(true);
    setLeftoverBudget(null);
    setFeeDiscountEligible(false);
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-leftover-budget`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ artist_id: artistId }),
      });

      if (!response.ok) {
        console.warn('Leftover budget: edge function failed');
        return;
      }

      const data = await response.json();
      console.log('💰 Leftover budget response:', data);

      if (data.has_leftover && data.leftover_budget > 0) {
        setLeftoverBudget(data.leftover_budget);
        setFeeDiscountEligible(!!data.fee_discount_eligible);
      } else {
        setLeftoverBudget(null);
        setFeeDiscountEligible(false);
      }
    } catch (error) {
      console.error('Error fetching leftover budget:', error);
      setLeftoverBudget(null);
    } finally {
      setLeftoverBudgetLoading(false);
    }
  };

  const selectExistingArtist = async (artist: any) => {
    console.log('🎯 Selecting existing artist:', artist);

    // Set selected artist ID and created date
    setFormData(prev => ({ ...prev, selected_artist_id: artist.id }));

    // Check for leftover budget from completed projects
    fetchLeftoverBudget(artist.id);

    try {
      // Fetch full artist and contact data from database
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/artists?id=eq.${artist.id}&select=*,customer_id,contacts:artist_contacts(contact_id,contacts(*,customers(*)))`, {
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        }
      });

      if (response.ok) {
        const artistData = await response.json();
        const fullArtist = artistData[0];
        const contact = fullArtist?.contacts?.[0]?.contacts;
        const customer = contact?.customers;

        console.log('📊 Full artist data:', fullArtist);
        console.log('📊 Contact data:', contact);
        console.log('📊 Customer data:', customer);

        // Pre-fill form data with all available information
        setFormData((prev: CampaignSubmissionData) => ({
          ...prev,
          kuenstlername: fullArtist.name || artist.name,
          vorname: contact?.first_name || '',
          nachname: contact?.last_name || '',
          firma: customer?.type === 'company' ? customer?.name : '',
          email: contact?.email || '',
          telefon: contact?.phone || formData.verification_phone,
          // Address data
          strasse: contact?.street || '',
          plz: contact?.zip || '',
          ort: contact?.city || '',
          land: contact?.country || 'Deutschland',
          // Social media URLs
          instagram_url: fullArtist.instagram_url || '',
          tiktok_url: fullArtist.tiktok_url || '',
          facebook_page_url: fullArtist.facebook_page_url || '',
          // Mark as existing artist
          selected_artist_id: artist.id
        }));

        // toast.success(`Künstler "${artist.name}" ausgewählt - Daten werden geladen`);
      } else {
        // Fallback to basic data if fetch fails
        setFormData((prev: CampaignSubmissionData) => ({
          ...prev,
          kuenstlername: artist.name,
          vorname: artist.contact?.first_name || '',
          nachname: artist.contact?.last_name || '',
          email: artist.contact?.email || '',
          telefon: artist.contact?.phone || formData.verification_phone,
          selected_artist_id: artist.id
        }));
        // toast.success(`Künstler "${artist.name}" ausgewählt`);
      }
    } catch (error) {
      console.error('Error fetching artist details:', error);
      // Fallback to basic data
      setFormData((prev: CampaignSubmissionData) => ({
        ...prev,
        kuenstlername: artist.name,
        telefon: formData.verification_phone,
        selected_artist_id: artist.id
      }));
      // toast.success(`Künstler "${artist.name}" ausgewählt`);
    }
    
    // Proceed to step 2 (contact data will be pre-filled)
    setCurrentStep(2);
  };

  const createNewArtist = () => {
    // Skip artist selection and proceed to contact data entry
    setCurrentStep(2);
    // toast.success('Neuer Künstler wird angelegt');
  };

  // ── Monday.com PM board import ──────────────────────────────────
  const fetchMondayItems = async (search: string = '') => {
    setMondayLoading(true);
    setMondayOffset(0);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/search-monday-items`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ search, limit: 10, offset: 0 }),
        }
      );
      if (!response.ok) throw new Error('Failed to fetch from Monday.com');
      const data = await response.json();
      setMondayItems(data.items || []);
      setMondayHasMore(data.has_more || false);
    } catch (error) {
      console.error('Error fetching Monday.com items:', error);
    } finally {
      setMondayLoading(false);
    }
  };

  const loadMoreMondayItems = async () => {
    if (mondayLoadingMore || !mondayHasMore) return;
    setMondayLoadingMore(true);
    const nextOffset = mondayOffset + 10;
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/search-monday-items`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ search: mondaySearch, limit: 10, offset: nextOffset }),
        }
      );
      if (!response.ok) throw new Error('Failed to fetch from Monday.com');
      const data = await response.json();
      setMondayItems(prev => [...prev, ...(data.items || [])]);
      setMondayHasMore(data.has_more || false);
      setMondayOffset(nextOffset);
    } catch (error) {
      console.error('Error loading more Monday.com items:', error);
    } finally {
      setMondayLoadingMore(false);
    }
  };

  const openMondayImport = () => {
    setShowMondayImport(true);
    fetchMondayItems('');
  };

  const selectMondayItem = (item: any) => {
    // Pre-fill the form with Monday.com data and advance to step 2
    const budgetEur = parseFloat(item.budget) || 0;
    const isNewCustomer = (item.is_new_customer || '').toLowerCase() === 'ja';
    const hasCreativeSupport = (item.creative_support || '').toLowerCase() === 'ja';

    setFormData(prev => ({
      ...prev,
      user_type: 'manager',
      customer_status: isNewCustomer ? 'new' : 'existing',
      kuenstlername: item.name || '',
      vorname: item.first_name || '',
      nachname: item.last_name || '',
      firma: item.company || '',
      email: item.email || '',
      telefon: item.phone || '',
      strasse: item.street || '',
      plz: item.zip || '',
      ort: item.city || '',
      land: item.country || 'Deutschland',
      release_name: item.release_name || '',
      release_date: item.release_date || '',
      cover_link: item.cover_link || '',
      master_datei_link: item.master_link || '',
      facebook_page_url: item.facebook_page_url || '',
      content_ordner: item.content_folder || '',
      voucher_promocode: item.voucher || '',
      werbebudget_netto: budgetEur,
      campaign_management: hasCreativeSupport,
      content_strategy_upsell: hasCreativeSupport,
      release_published: 'no',
    }));

    setShowMondayImport(false);
    setCurrentStep(2);
  };

  const fetchSpotifyTrackData = async (trackUrl: string) => {
    if (!trackUrl || !trackUrl.includes('spotify.com/track/')) {
      return;
    }

    setFetchingSpotifyTrack(true);
    setSpotifyTrackData(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-spotify-track`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ 
            spotify_track_url: trackUrl
          })
        }
      );

      if (!response.ok) {
        throw new Error('Fehler beim Abrufen der Track-Daten');
      }

      const result = await response.json();
      const trackData = result.data;

      setSpotifyTrackData(trackData);

      // Auto-fill form data
      setFormData(prev => ({
        ...prev,
        release_name: trackData.name,
        release_date: trackData.release_date || '',
        cover_link: trackData.cover_image || '',
        spotify_uri: trackData.spotify_uri || ''
      }));

      // toast.success(`Track "${trackData.name}" erfolgreich geladen!`);
    } catch (error) {
      console.error('Error fetching Spotify track:', error);
      // toast.error('Fehler beim Laden der Track-Daten. Bitte überprüfe die URL.');
    } finally {
      setFetchingSpotifyTrack(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    setFormData((prev: CampaignSubmissionData) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));

    // Auto-advance to customer status step when user_type is selected
    if (name === 'user_type' && (value === 'artist' || value === 'manager')) {
      setTimeout(() => {
        setCurrentStep(1.5); // Go to customer status step
      }, 300); // Small delay for smooth transition
    }
    
    // Auto-advance from customer status to appropriate next step
    if (name === 'customer_status' && (value === 'new' || value === 'existing')) {
      setTimeout(() => {
        if (value === 'new') {
          setCurrentStep(2); // Go directly to contact data for new customers
        } else {
          // Reset verification method so chooser shows
          setFormData(prev => ({ ...prev, verification_method: '' as any }));
          setCurrentStep(1.7); // Go to verification method chooser
        }
      }, 300);
    }
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData((prev: CampaignSubmissionData) => ({
      ...prev,
      [name]: checked
    }));
  };

  const validateStep = (step: number): boolean => {
    // Authenticated (admin) users skip all field validation
    if (currentUser) return true;

    switch (step) {
      case 1:
      case 1.5:
      case 1.7:
        // Step 1 variants: Wer bist du? - phone verification required for 1.7
        if (step === 1.7) {
          console.log('🔍 Step 1.7 validation:', {
            is_phone_verified: formData.is_phone_verified,
            artistLookupComplete,
            associatedArtistsLength: associatedArtists.length,
            selected_artist_id: formData.selected_artist_id,
            user_type: formData.user_type
          });
          // Must be verified and artist lookup complete before allowing to proceed
          if (!formData.is_phone_verified || !artistLookupComplete) {
            console.log('🔍 Validation failed: not verified or lookup not complete');
            return false;
          }
          // If artists were found, require one to be selected
          if (associatedArtists.length > 0) {
            const valid = !!(formData.user_type && formData.selected_artist_id);
            console.log('🔍 Artists found, requires selection:', valid);
            return valid;
          }
          // If no artists found, allow proceeding to create new artist
          console.log('🔍 No artists found, allowing to proceed');
          return !!(formData.user_type);
        }
        return !!(formData.user_type);
      case 2:
        // Step 2: Künstler & Kontaktdaten - firma is optional
        return !!(formData.kuenstlername && formData.vorname && formData.nachname && 
                 formData.email && formData.telefon);
      case 3:
        // Step 3: Rechungs-Adresse - VAT ID required if not Deutschland
        const addressFieldsValid = !!(formData.strasse && formData.plz && formData.ort && formData.land);
        return addressFieldsValid;
      case 4:
        // Step 4: Release-Informationen
        if (!formData.release_published) return false;
        if (formData.release_published === 'yes') {
          return !!formData.spotify_track_url;
        } else {
          return !!(formData.release_name && formData.release_date);
        }
      case 5:
        // Step 5: Artist Socials - all optional
        return true;
      case 6:
        // Step 6: Budget & Zusatzleistungen
        return formData.werbebudget_netto >= 650 && !!(customAgencyFee || formData.campaign_management || formData.content_strategy_upsell);
      default:
        return true;
    }
  };

  const nextStep = async () => {
    console.log('🚀 nextStep called - currentStep:', currentStep);
    console.log('🚀 Phone verified:', formData.is_phone_verified);
    console.log('🚀 Verification method:', formData.verification_method);
    console.log('🚀 Verification phone:', formData.verification_phone);
    console.log('🚀 Verification email:', formData.verification_email);
    console.log('🚀 codeSent:', codeSent);
    console.log('🚀 emailCodeSent:', emailCodeSent);
    console.log('🚀 Verification code:', formData.verification_code);
    console.log('🚀 Code length:', formData.verification_code?.length);

    // If on step 1.7 and phone verification is needed
    if (currentStep === 1.7 && !formData.is_phone_verified && formData.verification_code && formData.verification_code.length === 6) {
      console.log('🚀 Triggering code verification...');
      await verifyCode();
      return;
    }

    // If on step 1.7 and phone verification mode, trigger WhatsApp code send
    if (currentStep === 1.7 && !formData.is_phone_verified && formData.verification_method === 'phone' && formData.verification_phone && !codeSent) {
      console.log('🚀 Triggering WhatsApp code send...');
      await sendWhatsAppVerification();
      return;
    }

    // If on step 1.7 and email verification mode, trigger email lookup
    if (currentStep === 1.7 && !formData.is_phone_verified && formData.verification_method === 'email' && formData.verification_email && !emailCodeSent) {
      console.log('🚀 Triggering email lookup...');
      await lookupArtistsByEmail(formData.verification_email);
      return;
    }

    console.log('🚀 Validating step:', currentStep);
    const isValid = validateStep(currentStep);
    console.log('🚀 Validation result:', isValid);
    if (isValid) {
      // ─── Step 2: Intercept for new customer phone verification ──────────
      // New customers (who skipped step 1.7) must verify their phone via
      // WhatsApp before proceeding to step 3. Existing customers already
      // verified on step 1.7.
      if (currentStep === 2 && !formData.is_phone_verified && !step2PhoneVerified) {
        console.log('🚀 Step 2: new customer, phone not verified — triggering WhatsApp verification');
        if (!step2CodeSent) {
          await sendStep2WhatsAppCode();
        }
        return; // Stay on step 2 — verification UI will appear
      }

      console.log('🚀 Step valid, moving to next step');
      // Handle decimal steps properly
      if (currentStep === 1) {
        setCurrentStep(1.5);
      } else if (currentStep === 1.5) {
        setCurrentStep(1.7);
      } else if (currentStep === 1.7) {
        console.log('🚀 Attempting to move from 1.7 to 2');
        setCurrentStep(2);
      } else {
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      }
    } else {
      console.log('🚀 Step validation failed, staying on current step');
      // toast.error('Bitte fülle alle Pflichtfelder aus');
    }
  };

  const prevStep = () => {
    if (currentStep === 1.7) {
      setFormData(prev => ({ ...prev, verification_method: '' as any }));
      setCurrentStep(1.5);
    } else if (currentStep === 1.5) {
      setCurrentStep(1);
    } else if (currentStep === 2) {
      // If user came from verification, go back to 1.7; if new customer, go to 1.5
      setCurrentStep(formData.customer_status === 'existing' ? 1.7 : 1.5);
    } else {
      setCurrentStep(prev => Math.max(prev - 1, 1));
    }
  };

  // ─── Auto-advance for single-choice card steps ──────────────────────────
  // Steps where the entire step is just picking one of N cards (no other
  // fields to fill). Clicking a card selects it and immediately advances.
  const selectCardAndAdvance = (field: 'user_type' | 'customer_status', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Use a microtask delay so the state update is processed before advancing
    setTimeout(() => {
      if (field === 'user_type') {
        setCurrentStep(1.5);
      } else if (field === 'customer_status') {
        if (value === 'existing') {
          // Authenticated users already have is_phone_verified=true from loadAllArtists,
          // so they go straight to step 1.7 where the artist list is shown.
          // Public users go to 1.7 where they must verify first.
          setCurrentStep(1.7);
        } else {
          // New customer → skip verification, go to step 2
          setCurrentStep(2);
        }
      }
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Guard against a double-submit (button clicked again while already sending).
    if (isSubmitting) {
      return;
    }

    // Never submit from anywhere but the last step — the booking is only ever
    // triggered by the explicit "Book campaign" button there.
    if (currentStep < totalSteps) {
      console.warn('🚫 Submit blocked: not on the final step (currentStep:', currentStep, ')');
      return;
    }

    // Prevent form submission if on verification step
    if (currentStep === 1.7 && !formData.is_phone_verified) {
      return;
    }

    // Validate all steps before submission
    for (let step = 1; step <= 5; step++) {
      if (!validateStep(step)) {
        // toast.error(`Bitte fülle alle Pflichtfelder in Schritt ${step} aus`);
        setCurrentStep(step);
        return;
      }
    }
    
    if (!currentUser && !formData.final_confirmation) {
      setShowConfirmationWarning(true);
      // toast.error('Bitte bestätige die Kampagnenbuchung');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // ─── Upload files to Supabase Storage (if provided) ────────────────
      // File objects can't be JSON-serialized, so we upload them first
      // and replace the file field with the resulting public URL.
      let masterFileUrl = formData.master_datei_link || '';
      let coverFileUrl = formData.cover_link || '';

      if (formData.master_datei_file) {
        const file = formData.master_datei_file;
        const ext = file.name.split('.').pop() || 'audio';
        const path = `masters/${Date.now()}-${Math.random().toString(36).substring(2, 10)}.${ext}`;
        console.log('📤 Uploading master file:', file.name, '→', path);
        const { error: uploadError } = await supabase.storage
          .from('campaign-uploads')
          .upload(path, file, { cacheControl: '3600', upsert: false });
        if (uploadError) {
          console.error('❌ Master file upload failed:', uploadError);
        } else {
          const { data: urlData } = supabase.storage.from('campaign-uploads').getPublicUrl(path);
          masterFileUrl = urlData.publicUrl;
          console.log('✅ Master file uploaded:', masterFileUrl);
        }
      }

      if (formData.cover_file) {
        const file = formData.cover_file;
        const ext = file.name.split('.').pop() || 'jpg';
        const path = `covers/${Date.now()}-${Math.random().toString(36).substring(2, 10)}.${ext}`;
        console.log('📤 Uploading cover file:', file.name, '→', path);
        const { error: uploadError } = await supabase.storage
          .from('campaign-uploads')
          .upload(path, file, { cacheControl: '3600', upsert: false });
        if (uploadError) {
          console.error('❌ Cover file upload failed:', uploadError);
        } else {
          const { data: urlData } = supabase.storage.from('campaign-uploads').getPublicUrl(path);
          coverFileUrl = urlData.publicUrl;
          console.log('✅ Cover file uploaded:', coverFileUrl);
        }
      }

      // For authenticated users, set user_type to 'manager' if not set
      const submissionData = {
        ...formData,
        // Replace File objects with uploaded URLs (remove File fields — can't JSON-serialize)
        master_datei_file: undefined,
        cover_file: undefined,
        master_datei_link: masterFileUrl,
        cover_link: coverFileUrl,
        // Normalize open.spotify.com links → spotify: URIs; invalid values
        // (e.g. pasted artist profile links) are dropped instead of stored.
        spotify_uri: normalizeSpotifyUri(formData.spotify_uri),
        user_type: formData.user_type || (currentUser ? 'manager' : ''),
        werbebudget_netto: formData.werbebudget_netto * 100,
        fee_discount_50: feeDiscountEligible,
      };
      
      console.log('Submitting campaign data:', submissionData);
      console.log('Using anon key:', import.meta.env.VITE_SUPABASE_ANON_KEY);
      
      const response = await fetch('https://wrlgoxbzlngdtomjhvnz.supabase.co/functions/v1/submit-campaign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(submissionData)
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error('Fehler beim Senden der Kampagne');
      }

      const result = await response.json();

      // Create Notion sheet for new artists or campaigns with content strategy upsell
      // Only if not ads only (customAgencyFee is null)
      const shouldCreateNotionSheet =
        (formData.customer_status === 'new' && !customAgencyFee) ||
        formData.content_strategy_upsell;

      if (shouldCreateNotionSheet && result.project_id) {
        console.log('📝 Creating Notion sheet for project:', result.project_id);
        try {
          const notionResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-notion-sheet`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({ project_id: result.project_id })
          });
          if (notionResponse.ok) {
            console.log('✅ Notion sheet created successfully');
          } else {
            console.error('❌ Failed to create Notion sheet:', await notionResponse.text());
          }
        } catch (error) {
          console.error('❌ Error creating Notion sheet:', error);
        }
      }

      // Store submitted data and show success screen
      const campaignFee = formData.customer_status === 'new' ? 500 : 400;
      const effectiveFee = feeDiscountEligible ? Math.round(campaignFee / 2) : campaignFee;
      setSubmittedData({
        release_id: result.release_id,
        artist_id: result.artist_id,
        artist_name: formData.kuenstlername,
        release_name: formData.release_name,
        release_date: formData.release_date,
        total_amount: ((formData.campaign_management && !customAgencyFee ? effectiveFee : 0) +
                        (formData.content_strategy_upsell && !customAgencyFee ? 1000 : 0) +
                        (customAgencyFee || 0) +
                        formData.werbebudget_netto),
        email: formData.email
      });
      setSubmissionSuccess(true);

      // Clear draft from localStorage — submission is complete
      localStorage.removeItem(DRAFT_KEY);
      console.log('💾 Campaign draft cleared from localStorage');

      // Don't reset form, keep data for reference
      
    } catch (error) {
      console.error('Error submitting campaign:', error);
      // toast.error('Fehler beim Senden der Kampagne. Bitte versuche es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success screen if submission was successful
  if (submissionSuccess && submittedData) {
    return (
      <div className="min-h-screen bg-black font-sans flex items-center justify-center">
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <div className="bg-black backdrop-blur-lg rounded-3xl p-12 border border-gray-800 shadow-2xl text-center">
            {/* Success Icon */}
            <div className="mb-8">
              <div className="w-24 h-24 bg-[#0000fe]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-[#0000fe]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold text-white mb-4">Campaign submitted successfully!</h1>
              <p className="text-xl text-gray-300 mb-8">
                Thank you for your booking. We have received your campaign and will contact you shortly.
              </p>
            </div>

            {/* Next Steps */}
            {/* <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6 mb-8 text-left">
              <h3 className="text-lg font-semibold text-white mb-3">What's next?</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">1.</span>
                  <span>You will receive a confirmation email at <strong>{submittedData.email}</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">2.</span>
                  <span>Our team reviews your campaign and will contact you within 24 hours</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">3.</span>
                  <span>After approval, we start with the campaign setup</span>
                </li>
              </ul>
            </div> */}

            {/* Action Button */}
            {/* <button
              onClick={() => window.location.href = '/'}
              className="px-8 py-4 text-white font-medium rounded-lg transition-colors hover:opacity-90"
              style={{ backgroundColor: '#0000fe' }}
            >
              Back to home
            </button> */}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black font-sans">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          {/* Back navigation for authenticated users */}
          {currentUser && (
            <div className="flex justify-start mb-4">
              <button
                type="button"
                onClick={() => window.location.href = '/dashboard'}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück zum Dashboard
              </button>
            </div>
          )}

          {/* Logo */}
          <div className="mb-8">
            <img 
              src="/plane_white.png" 
              alt="SwipeUp Logo" 
              className="h-14 mx-auto"
            />
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4">
            NEW CAMPAIGN
          </h1>
          <p className="text-base text-white max-w-3xl mx-auto">
            Please provide us with all information about your next release so we can prepare your campaign and schedule your release on time.
          </p>
        </div>

        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep 
                    ? 'text-white bg-[#0000fe]' 
                    : 'text-white bg-gray-800'
                }`}>
                  {step}
                </div>
                {step < 5 && (
                  <div className={`h-1 w-20 mx-2 ${
                    step < currentStep ? '' : 'bg-gray-800'
                  }`} style={{
                    backgroundColor: step < currentStep ? '#0000fe' : undefined
                  }} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-400">
            <span>Who are you?</span>
            <span>Contact</span>
            <span>Billing</span>
            <span>Release</span>
            <span>Budget</span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-black backdrop-blur-lg rounded-3xl p-8 border border-gray-800 shadow-2xl">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Who are you? OR Artist Selection for Authenticated Users */}
              {currentStep === 1 && !currentUser && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <User className="w-12 h-12 text-[#0000fe] mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Who are you?</h2>
                    <p className="text-white">Please select who is creating the campaign</p>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      <label className={`flex items-center p-6 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.user_type === 'artist' 
                          ? 'border-[#0000fe] bg-[#0000fe]/10' 
                          : 'border-gray-800 hover:border-[#0000fe]'
                      }`} onClick={() => selectCardAndAdvance('user_type', 'artist')}>
                        <input
                          type="radio"
                          name="user_type"
                          value="artist"
                          checked={formData.user_type === 'artist'}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="flex items-center w-full">
                          <User className="w-8 h-8 text-[#0000fe] mr-4" />
                          <div>
                            <h3 className="text-lg font-semibold text-white">Artist</h3>
                            <p className="text-white text-sm">I am creating the campaign for myself</p>
                          </div>
                        </div>
                      </label>

                      <label className={`flex items-center p-6 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.user_type === 'manager' 
                          ? 'border-[#0000fe] bg-[#0000fe]/10' 
                          : 'border-gray-800 hover:border-[#0000fe]'
                      }`} onClick={() => selectCardAndAdvance('user_type', 'manager')}>
                        <input
                          type="radio"
                          name="user_type"
                          value="manager"
                          checked={formData.user_type === 'manager'}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="flex items-center w-full">
                          <Building className="w-8 h-8 text-[#0000fe] mr-4" />
                          <div>
                            <h3 className="text-lg font-semibold text-white">Management/Label</h3>
                            <p className="text-white text-sm">I am creating the campaign on behalf of my artist</p>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Artist Selection for Authenticated Users */}
              {currentStep === 1 && currentUser && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Music className="w-12 h-12 text-[#0000fe] mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Select artist</h2>
                    <p className="text-white">Select an artist or create a new one</p>
                  </div>

                  {isLoadingArtists ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Loader className="w-12 h-12 text-[#0000fe] animate-spin mb-4" />
                      <p className="text-white">Loading artists...</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {associatedArtists.length > 0 && (
                        <>
                          {/* Search Bar */}
                          <div className="mb-4">
                            <input
                              type="text"
                              value={artistSearchQuery}
                              onChange={(e) => setArtistSearchQuery(e.target.value)}
                              placeholder="Search artist..."
                              className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0000fe] focus:border-transparent"
                            />
                          </div>
                          
                          {/* Artist List */}
                          <div className="max-h-96 overflow-y-auto space-y-3">
                            {associatedArtists
                              .filter(artist => 
                                artist.name.toLowerCase().includes(artistSearchQuery.toLowerCase())
                              )
                              .map((artist, index) => (
                                <div key={index} className="p-4 bg-gray-900 border border-gray-800 rounded-lg cursor-pointer hover:border-[#0000fe] transition-colors">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h4 className="font-semibold text-white">{artist.name}</h4>
                                      {artist.instagram_url && (
                                        <p className="text-sm text-gray-400">Instagram: {artist.instagram_url.split('/').pop()}</p>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => selectExistingArtist(artist)}
                                      className="px-4 py-2 text-white font-medium rounded-lg transition-colors" style={{ backgroundColor: '#0000fe' }}
                                    >
                                      Select
                                    </button>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </>
                      )}
                      
                      <button
                        type="button"
                        onClick={createNewArtist}
                        className="w-full px-6 py-3 text-white font-medium rounded-lg border border-gray-600 hover:border-[#0000fe] transition-colors bg-transparent"
                      >
                        Neuen Künstler & Release anlegen
                      </button>

                      {/* Admin: Import from Monday.com PM board */}
                      <button
                        type="button"
                        onClick={openMondayImport}
                        className="w-full px-6 py-3 text-white font-medium rounded-lg border border-gray-600 hover:border-[#0000fe] transition-colors bg-transparent flex items-center justify-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Aus Monday.com importieren
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 1.5: Customer Status */}
              {currentStep === 1.5 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Building className="w-12 h-12 text-[#0000fe] mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">New customer / Existing customer?</h2>
                    <p className="text-white">Have we worked with this artist before?</p>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      <label className={`flex items-center p-6 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.customer_status === 'existing' 
                          ? 'border-[#0000fe] bg-[#0000fe]/10' 
                          : 'border-gray-800 hover:border-[#0000fe]'
                      }`} onClick={() => selectCardAndAdvance('customer_status', 'existing')}>
                        <input
                          type="radio"
                          name="customer_status"
                          value="existing"
                          checked={formData.customer_status === 'existing'}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="flex items-center w-full">
                          <User className="w-8 h-8 text-green-400 mr-4" />
                          <div>
                            <h3 className="text-lg font-semibold text-white">Yes</h3>
                            <p className="text-white text-sm">We have worked together before</p>
                          </div>
                        </div>
                      </label>

                      <label className={`flex items-center p-6 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.customer_status === 'new' 
                          ? 'border-[#0000fe] bg-[#0000fe]/10' 
                          : 'border-gray-800 hover:border-[#0000fe]'
                      }`} onClick={() => selectCardAndAdvance('customer_status', 'new')}>
                        <input
                          type="radio"
                          name="customer_status"
                          value="new"
                          checked={formData.customer_status === 'new'}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="flex items-center w-full">
                          <Building className="w-8 h-8 text-[#0000fe] mr-4" />
                          <div>
                            <h3 className="text-lg font-semibold text-white">No</h3>
                            <p className="text-white text-sm">This is our first collaboration</p>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 1.7: Verification (for existing customers) */}
              {currentStep === 1.7 && (
                <div className="space-y-6">
                  {/* ─── Verification UI (only if NOT verified) ─────────────── */}
                  {!formData.is_phone_verified && (
                    <>
                  {!formData.verification_method && (
                    <div className="text-center mb-8">
                      <BadgeCheck className="w-12 h-12 text-[#0000fe] mx-auto mb-4" />
                      <h2 className="text-2xl font-bold text-white mb-2">Verifizierung</h2>
                      <p className="text-sm text-white mb-6">How would you like to verify?</p>
                      
                      <div className="grid grid-cols-1 gap-4 max-w-md mx-auto">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, verification_method: 'phone' }))}
                          className="flex items-center p-6 border-2 border-gray-800 rounded-lg hover:border-[#0000fe] transition-all text-left"
                        >
                          <svg className="w-8 h-8 text-green-400 mr-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                          </svg>
                          <div>
                            <h3 className="text-lg font-semibold text-white">WhatsApp</h3>
                            <p className="text-white text-sm">The number must be a member of the corresponding artist WhatsApp group</p>
                          </div>
                        </button>

                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, verification_method: 'email' }))}
                          className="flex items-center p-6 border-2 border-gray-800 rounded-lg hover:border-[#0000fe] transition-all text-left"
                        >
                          <Mail className="w-8 h-8 text-[#0000fe] mr-4 flex-shrink-0" />
                          <div>
                            <h3 className="text-lg font-semibold text-white">E-Mail</h3>
                            <p className="text-white text-sm">The email must already be linked to the artist in the system</p>
                          </div>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, is_phone_verified: true }));
                            setCurrentStep(2);
                          }}
                          className="flex items-center p-6 border-2 border-gray-800 rounded-lg hover:border-[#0000fe] transition-all text-left"
                        >
                          <User className="w-8 h-8 text-gray-400 mr-4 flex-shrink-0" />
                          <div>
                            <h3 className="text-lg font-semibold text-white">Without verification</h3>
                            <p className="text-white text-sm">Requires re-entering all data</p>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* WhatsApp Verification Flow */}
                  {formData.verification_method === 'phone' && !formData.is_phone_verified && !artistLookupComplete && (
                    <div className="space-y-6">
                      <div className="text-center mb-4">
                        <svg className="w-12 h-12 text-green-400 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                        </svg>
                        <h2 className="text-2xl font-bold text-white mb-2">WhatsApp Verification</h2>
                        <p className="text-sm text-white">Please enter your mobile number so we can send you a confirmation code via WhatsApp</p>
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, verification_method: '' }))} className="text-sm text-gray-400 hover:text-white mt-2 underline">
                          Choose other method
                        </button>
                      </div>

                      {isSendingWhatsAppCode ? (
                        <div className="flex flex-col items-center justify-center py-8">
                          <Loader className="w-12 h-12 text-[#0000fe] animate-spin mb-4" />
                          <p className="text-white">Sending code via WhatsApp...</p>
                        </div>
                      ) : !codeSent && (
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            <svg className="w-4 h-4 inline mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.0 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.0 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                            Mobile number *
                          </label>
                          <input
                            type="tel"
                            name="verification_phone"
                            value={formData.verification_phone}
                            onChange={handleInputChange}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                            }}
                            className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                            placeholder="+44 7700 900123"
                            required
                          />
                          {formData.verification_phone && toE164(formData.verification_phone) && (
                            <p className="text-xs text-gray-400 mt-1">
                              → {toE164(formData.verification_phone)}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Code Entry */}                      {codeSent && (
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            <svg className="w-4 h-4 inline mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            Enter code
                          </label>
                          <input
                            type="text"
                            name="verification_code"
                            value={formData.verification_code}
                            onChange={handleInputChange}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                            }}
                            className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2 text-center text-2xl tracking-widest" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                            placeholder="000000"
                            maxLength={6}
                            required
                          />
                          <p className="text-xs text-gray-400 mt-2">
                            Enter the 6-digit code from your WhatsApp message
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Email Verification Flow */}
                  {formData.verification_method === 'email' && !formData.is_phone_verified && !artistLookupComplete && (
                    <div className="space-y-6">
                      <div className="text-center mb-4">
                        <Mail className="w-12 h-12 text-[#0000fe] mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-white mb-2">Email Verification</h2>
                        <p className="text-sm text-white">
                          {emailCodeSent
                            ? 'We have sent a 6-digit code to your email'
                            : 'Please enter your email address so we can send you a confirmation code'}
                        </p>
                        <button type="button" onClick={() => { setFormData(prev => ({ ...prev, verification_method: '' as any })); setEmailCodeSent(false); setEmailVerificationCode(''); }} className="text-sm text-gray-400 hover:text-white mt-2 underline">
                          Choose another method
                        </button>
                      </div>

                      {!emailCodeSent && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-white mb-2">
                              <Mail className="w-4 h-4 inline mr-2" />
                              Email address *
                            </label>
                            <input
                              type="email"
                              name="verification_email"
                              value={formData.verification_email}
                              onChange={handleInputChange}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                }
                              }}
                              className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                              placeholder="your@email.de"
                              required
                            />
                            <p className="text-xs text-gray-400 mt-1">
                              We check if this email is associated with an artist
                            </p>
                          </div>

                        </>
                      )}

                      {emailCodeSent && (
                        <>
                          <div className="bg-[#0000fe]/10 border border-[#0000fe]/20 rounded-lg p-4 text-center">
                            <p className="text-sm text-white">
                              Code sent to <strong>{formData.verification_email}</strong>
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-white mb-2">
                              Confirmation code
                            </label>
                            <input
                              type="text"
                              value={emailVerificationCode}
                              onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9]/g, '');
                                setEmailVerificationCode(val);
                                if (val.length === 6) {
                                  verifyEmailCode(val);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                }
                              }}
                              className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2 text-center text-2xl tracking-widest" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                              placeholder="000000"
                              maxLength={6}
                            />
                            <p className="text-xs text-gray-400 mt-1">
                              Enter the 6-digit code from your email
                            </p>
                          </div>

                          <div className="text-center">
                            <button
                              type="button"
                              onClick={() => { setEmailCodeSent(false); setEmailVerificationCode(''); }}
                              className="text-sm text-gray-400 hover:text-white underline"
                            >
                              Resend
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                    </>
                  )}

                  {/* Artist Selection (shown ONLY after verification is complete) */}
                  {currentStep === 1.7 && formData.is_phone_verified && (
                    <div className="space-y-6 mt-6">
                      <div className="text-center p-6 bg-[#0000fe]/10 border border-[#0000fe]/20 rounded-lg">
                        <h3 className="text-lg font-semibold text-white mb-2">Select artist</h3>
                        
                        {isLoadingArtists ? (
                          <div className="flex flex-col items-center justify-center py-8">
                            <Loader className="w-12 h-12 text-[#0000fe] animate-spin mb-4" />
                            <p className="text-white">Loading associated artists...</p>
                          </div>
                        ) : (
                        <div className="space-y-4">
                          <p className="text-white">Select an artist or create a new one:</p>

                          {associatedArtists.length > 0 && (
                            <>
                              {/* Search Bar */}
                              <div className="mb-4">
                                <input
                                  type="text"
                                  value={artistSearchQuery}
                                  onChange={(e) => setArtistSearchQuery(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                                  placeholder="Search artist..."
                                  className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0000fe] focus:border-transparent"
                                />
                              </div>
                              
                              {/* Artist List */}
                              <div className="max-h-96 overflow-y-auto space-y-3">
                                {associatedArtists
                                  .filter(artist => 
                                    artist.name.toLowerCase().includes(artistSearchQuery.toLowerCase()) ||
                                    (artist.contact?.email || artist.email || '').toLowerCase().includes(artistSearchQuery.toLowerCase())
                                  )
                                  .map((artist, index) => (
                                    <div key={index} className="p-4 bg-gray-900 border border-gray-800 rounded-lg cursor-pointer hover:border-[#0000fe] transition-colors">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <h4 className="font-semibold text-white">{artist.name}</h4>
                                          <p className="text-sm text-gray-400">{artist.contact?.email || artist.email}</p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => selectExistingArtist(artist)}
                                          className="px-4 py-2 text-white font-medium rounded-lg transition-colors" style={{ backgroundColor: '#0000fe' }}
                                        >
                                          Select
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </>
                          )}
                          
                          <button
                            type="button"
                            onClick={createNewArtist}
                            className="w-full px-6 py-3 text-white font-medium rounded-lg border border-gray-600 hover:border-[#0000fe] transition-colors bg-transparent"
                          >
                            Create new artist & release
                          </button>
                        </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Artist & Contact Data */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <User className="w-12 h-12 text-[#0000fe] mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {formData.user_type === 'artist' ? 'Your Data' : 'Artist & Contact Data'}
                    </h2>
                    <p className="text-white">
                      {formData.user_type === 'artist'
                        ? 'Please provide your basic information'
                        : 'Please provide the artist\'s information and your contact details'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        <User className="w-4 h-4 inline mr-2" />
                        Artist name *
                      </label>
                      <input
                        type="text"
                        name="kuenstlername"
                        value={formData.kuenstlername}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="Your artist name"
                        maxLength={255}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        First name *
                      </label>
                      <input
                        type="text"
                        name="vorname"
                        value={formData.vorname}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="Your first name"
                        required
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        For personal information, please provide the billing recipient's details.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Last name *
                      </label>
                      <input
                        type="text"
                        name="nachname"
                        value={formData.nachname}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="Your last name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        <Building className="w-4 h-4 inline mr-2" />
                        Company
                      </label>
                      <input
                        type="text"
                        name="firma"
                        value={formData.firma}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="Company name (optional)"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        <Mail className="w-4 h-4 inline mr-2" />
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="your@email.com"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        <Phone className="w-4 h-4 inline mr-2" />
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="telefon"
                        value={formData.telefon}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Reset verification state if phone changes after verification
                          if (step2CodeSent || step2PhoneVerified) {
                            setStep2CodeSent(false);
                            setStep2PhoneVerified(false);
                            setStep2CodeInput('');
                            setStep2GeneratedCode('');
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="+44 7700 900123"
                        required
                        disabled={step2PhoneVerified}
                      />
                      {formData.telefon && toE164(formData.telefon) && (
                        <p className="text-xs text-gray-400 mt-1">
                          → {toE164(formData.telefon)}
                        </p>
                      )}
                      {step2PhoneVerified && (
                        <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                          <BadgeCheck className="w-3 h-3" /> Phone verified
                        </p>
                      )}
                    </div>
                  </div>

                  {/* WhatsApp phone verification for new customers */}
                  {step2CodeSent && !step2PhoneVerified && (
                    <div className="bg-[#0000fe]/10 border border-[#0000fe]/30 rounded-lg p-6 mt-4">
                      <div className="flex items-start gap-3 mb-4">
                        <svg className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                        </svg>
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-white">Verify your phone number</h3>
                          <p className="text-xs text-white mt-1">
                            We sent a 6-digit code via WhatsApp to {formData.telefon}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={step2CodeInput}
                          onChange={(e) => setStep2CodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); verifyStep2Code(); } }}
                          placeholder="6-digit code"
                          className="flex-1 px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2 text-center text-lg tracking-widest"
                          style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                          maxLength={6}
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={verifyStep2Code}
                          disabled={step2CodeInput.length !== 6 || step2Verifying}
                          className="px-6 py-3 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: '#0000fe' }}
                        >
                          {step2Verifying ? <Loader className="w-5 h-5 animate-spin" /> : 'Verify'}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setStep2CodeSent(false);
                          setStep2CodeInput('');
                          setStep2GeneratedCode('');
                        }}
                        className="text-xs text-gray-400 mt-3 hover:text-white transition-colors"
                      >
                        Change phone number
                      </button>
                    </div>
                  )}
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <MapPin className="w-12 h-12 text-[#0000fe] mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Billing Address</h2>
                    <p className="text-white">We need your address for billing</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-white mb-2">
                        <MapPin className="w-4 h-4 inline mr-2" />
                        Street & house number *
                      </label>
                      <input
                        type="text"
                        name="strasse"
                        value={formData.strasse}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="Example Street 123"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Postal code *
                      </label>
                      <input
                        type="text"
                        name="plz"
                        value={formData.plz}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="12345"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="ort"
                        value={formData.ort}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="Example City"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-white mb-2">
                        Country *
                      </label>
                      <select
                        name="land"
                        value={formData.land}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        required
                      >
                        <option value="Germany">Germany</option>
                        <option value="Austria">Austria</option>
                        <option value="Switzerland">Switzerland</option>
                        <option value="Belgium">Belgium</option>
                        <option value="Netherlands">Netherlands</option>
                        <option value="France">France</option>
                        <option value="Italy">Italy</option>
                        <option value="Spain">Spain</option>
                        <option value="Portugal">Portugal</option>
                        <option value="Poland">Poland</option>
                        <option value="Czech Republic">Czech Republic</option>
                        <option value="Hungary">Hungary</option>
                        <option value="Slovakia">Slovakia</option>
                        <option value="Slovenia">Slovenia</option>
                        <option value="Croatia">Croatia</option>
                        <option value="Denmark">Denmark</option>
                        <option value="Sweden">Sweden</option>
                        <option value="Norway">Norway</option>
                        <option value="Finland">Finland</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Luxembourg">Luxembourg</option>
                        <option value="Estonia">Estonia</option>
                        <option value="Latvia">Latvia</option>
                        <option value="Lithuania">Lithuania</option>
                        <option value="Romania">Romania</option>
                        <option value="Bulgaria">Bulgaria</option>
                        <option value="Greece">Greece</option>
                        <option value="Cyprus">Cyprus</option>
                        <option value="Malta">Malta</option>
                        <option value="USA">USA</option>
                        <option value="Canada">Canada</option>
                        <option value="Australia">Australia</option>
                        <option value="New Zealand">New Zealand</option>
                        <option value="Japan">Japan</option>
                        <option value="South Korea">South Korea</option>
                        <option value="Singapore">Singapore</option>
                        <option value="Hong Kong">Hong Kong</option>
                        <option value="Brazil">Brazil</option>
                        <option value="Mexico">Mexico</option>
                        <option value="Argentina">Argentina</option>
                        <option value="Chile">Chile</option>
                        <option value="Colombia">Colombia</option>
                        <option value="Peru">Peru</option>
                        <option value="South Africa">South Africa</option>
                        <option value="Israel">Israel</option>
                        <option value="Turkey">Turkey</option>
                        <option value="Russia">Russia</option>
                        <option value="Ukraine">Ukraine</option>
                        <option value="Serbia">Serbia</option>
                        <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                        <option value="North Macedonia">North Macedonia</option>
                        <option value="Montenegro">Montenegro</option>
                        <option value="Albania">Albania</option>
                        <option value="Kosovo">Kosovo</option>
                        <option value="Moldova">Moldova</option>
                        <option value="Belarus">Belarus</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Armenia">Armenia</option>
                        <option value="Azerbaijan">Azerbaijan</option>
                        <option value="Kazakhstan">Kazakhstan</option>
                        <option value="China">China</option>
                        <option value="India">India</option>
                        <option value="Thailand">Thailand</option>
                        <option value="Vietnam">Vietnam</option>
                        <option value="Philippines">Philippines</option>
                        <option value="Indonesia">Indonesia</option>
                        <option value="Malaysia">Malaysia</option>
                        <option value="Taiwan">Taiwan</option>
                        <option value="Egypt">Egypt</option>
                        <option value="Morocco">Morocco</option>
                        <option value="Tunisia">Tunisia</option>
                        <option value="Algeria">Algeria</option>
                        <option value="Nigeria">Nigeria</option>
                        <option value="Kenya">Kenya</option>
                        <option value="Ghana">Ghana</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* VAT ID field - mandatory for EU (not DE), optional for non-EU and Germany */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-white mb-2">
                        <Building className="w-4 h-4 inline mr-2" />
                        VAT ID / Tax ID {(() => {
                          const EU_FOR_VAT = new Set(['Austria','Belgium','Bulgaria','Croatia','Cyprus','Czech Republic','Denmark','Estonia','Finland','France','Greece','Hungary','Ireland','Italy','Latvia','Lithuania','Luxembourg','Malta','Netherlands','Poland','Portugal','Romania','Slovakia','Slovenia','Spain','Sweden']);
                          return EU_FOR_VAT.has(formData.land) ? '*' : '(optional)';
                        })()}
                      </label>
                      <input
                        type="text"
                        name="vat_id"
                        value={formData.vat_id}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="DE123456789 / GB123456789"
                        required={(() => {
                          const EU_FOR_VAT = new Set(['Austria','Belgium','Bulgaria','Croatia','Cyprus','Czech Republic','Denmark','Estonia','Finland','France','Greece','Hungary','Ireland','Italy','Latvia','Lithuania','Luxembourg','Malta','Netherlands','Poland','Portugal','Romania','Slovakia','Slovenia','Spain','Sweden']);
                          return EU_FOR_VAT.has(formData.land);
                        })()}
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        {(() => {
                          const EU_FOR_VAT = new Set(['Austria','Belgium','Bulgaria','Croatia','Cyprus','Czech Republic','Denmark','Estonia','Finland','France','Greece','Hungary','Ireland','Italy','Latvia','Lithuania','Luxembourg','Malta','Netherlands','Poland','Portugal','Romania','Slovakia','Slovenia','Spain','Sweden']);
                          if (EU_FOR_VAT.has(formData.land)) {
                            return 'Required: Please provide your VAT ID for reverse charge (0% VAT).';
                          }
                          return 'Optional: VAT ID / Tax ID for your records.';
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Music className="w-12 h-12 text-[#0000fe] mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Release Information</h2>
                    <p className="text-white">All information about your release</p>
                  </div>

                  {/* Question: Is release already published? */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-white mb-4">
                      Is the release already published? *
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="release_published"
                          value="yes"
                          checked={formData.release_published === 'yes'}
                          onChange={handleInputChange}
                          className="w-5 h-5 text-[#0000fe] bg-gray-900 border-gray-800 focus:ring-[#0000fe]"
                        />
                        <span className="ml-3 text-white">Yes, already published</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="release_published"
                          value="no"
                          checked={formData.release_published === 'no'}
                          onChange={handleInputChange}
                          className="w-5 h-5 text-[#0000fe] bg-gray-900 border-gray-800 focus:ring-[#0000fe]"
                        />
                        <span className="ml-3 text-white">No, not yet published</span>
                      </label>
                    </div>
                  </div>

                  {/* If published: Show Spotify Track URL field */}
                  {formData.release_published === 'yes' && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          <Music className="w-4 h-4 inline mr-2" />
                          Spotify Track URL *
                        </label>
                        <input
                          type="url"
                          name="spotify_track_url"
                          value={formData.spotify_track_url}
                          onChange={handleInputChange}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                          onPaste={(e) => {
                            setTimeout(() => {
                              const url = (e.target as HTMLInputElement).value;
                              fetchSpotifyTrackData(url);
                            }, 100);
                          }}
                          onBlur={(e) => {
                            if (e.target.value && !spotifyTrackData) {
                              fetchSpotifyTrackData(e.target.value);
                            }
                          }}
                          className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" 
                          style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                          placeholder="https://open.spotify.com/track/..."
                          required
                          disabled={fetchingSpotifyTrack}
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          {fetchingSpotifyTrack ? 'Lade Track-Daten...' : 'Füge die Spotify Track URL ein - Daten werden automatisch geladen'}
                        </p>
                      </div>

                      {/* Display fetched track data */}
                      {spotifyTrackData && (
                        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                          <h3 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center">
                            <Music className="h-4 w-4 mr-2" />
                            Track-Informationen
                          </h3>
                          <div className="flex gap-4">
                            {spotifyTrackData.cover_image && (
                              <img 
                                src={spotifyTrackData.cover_image} 
                                alt="Cover" 
                                className="w-24 h-24 rounded-lg object-cover"
                              />
                            )}
                            <div className="flex-1 space-y-2">
                              <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Track Name:</p>
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">{spotifyTrackData.name}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Artist:</p>
                                <p className="text-sm text-gray-900 dark:text-white">{spotifyTrackData.artist}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Release-Datum:</p>
                                <p className="text-sm text-gray-900 dark:text-white">
                                  {new Date(spotifyTrackData.release_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* If not published: Show all release fields */}
                  {formData.release_published === 'no' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-white mb-2">
                        <Music className="w-4 h-4 inline mr-2" />
                        Release name *
                      </label>
                      <input
                        type="text"
                        name="release_name"
                        value={formData.release_name}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="Song title (without feat. XY)"
                        required
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        For songs with feature artists, please only provide the song title, not (feat. XY).
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        <Calendar className="w-4 h-4 inline mr-2" />
                        Release date *
                      </label>
                      <div className="relative">
                        <Calendar className="w-5 h-5 text-white absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                        <input
                          type="date"
                          name="release_date"
                          value={formData.release_date}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            // If the new release date is less than 4 weeks away, uncheck Content Strategy
                            const fourWeeksInMs = 28 * 24 * 60 * 60 * 1000;
                            const releaseDate = newDate ? new Date(newDate) : null;
                            const now = new Date();
                            now.setHours(0, 0, 0, 0);
                            const isAtLeast4WeeksOut = releaseDate ? (releaseDate.getTime() - now.getTime()) >= fourWeeksInMs : false;
                            setFormData(prev => ({
                              ...prev,
                              release_date: newDate,
                              content_strategy_upsell: isAtLeast4WeeksOut ? prev.content_strategy_upsell : false,
                            }));
                          }}
                          className="w-full pl-12 pr-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2 [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                          style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                          required
                        />
                      </div>
                      {formData.release_date && (
                        <p className="text-xs text-gray-400 mt-1">
                          Selected date: {new Date(formData.release_date).toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </p>
                      )}
                    </div>


                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-white mb-2">
                        <Upload className="w-4 h-4 inline mr-2" />
                        Master file
                      </label>
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <input
                            type="file"
                            name="master_datei_file"
                            accept="audio/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setFormData(prev => ({ ...prev, master_datei_file: file }));
                              }
                            }}
                            className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="url"
                            name="master_datei_link"
                            value={formData.master_datei_link}
                            onChange={handleInputChange}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                            className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                            placeholder="Dropbox, Google Drive, etc."
                          />
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-white mb-2">
                        <Upload className="w-4 h-4 inline mr-2" />
                        Cover-Artwork
                      </label>
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <input
                            type="file"
                            name="cover_file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setFormData(prev => ({ ...prev, cover_file: file }));
                              }
                            }}
                            className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="url"
                            name="cover_link"
                            value={formData.cover_link}
                            onChange={handleInputChange}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                            className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                            placeholder="Dropbox, Google Drive, etc."
                          />
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-white mb-2">
                        <Music className="w-4 h-4 inline mr-2" />
                        Spotify URI (optional)
                      </label>
                      <input
                        type="text"
                        name="spotify_uri"
                        value={formData.spotify_uri}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="spotify:track:4iV5W9uYEdYUVa79Axb7Rh"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Die Spotify URI deines Releases (falls bereits verfügbar) —
                        Guide: <a href="https://swipe.fm/spotify-uri" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">swipe.fm/spotify-uri</a>
                      </p>
                      {formData.spotify_uri.trim() && !normalizeSpotifyUri(formData.spotify_uri) && (
                        <p className="text-xs text-red-400 mt-1">
                          Kein gültiger Spotify Track/Album-URI — wird nicht gespeichert.
                        </p>
                      )}
                    </div>
                  </div>
                  )}
                </div>
              )}

              {/* Step 5: Artist Socials */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Music className="w-12 h-12 text-[#0000fe] mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Social Media Profile</h2>
                    <p className="text-white">Your social media channels for the campaign</p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Instagram profile URL
                      </label>
                      <input
                        type="url"
                        name="instagram_url"
                        value={formData.instagram_url}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" 
                        style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="https://instagram.com/yourprofile"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Your Instagram profile for the campaign
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        TikTok profile URL
                      </label>
                      <input
                        type="url"
                        name="tiktok_url"
                        value={formData.tiktok_url}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" 
                        style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="https://tiktok.com/@yourprofile"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Optional: Your TikTok profile
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Facebook Fan Page URL
                      </label>
                      <input
                        type="url"
                        name="facebook_page_url"
                        value={formData.facebook_page_url}
                        onChange={handleInputChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" 
                        style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                        placeholder="https://facebook.com/yourpage"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Required for Instagram ad access. If you don't have one, please create a Facebook Fan Page.
                        <br />⚠️ Please never share login information (passwords, etc.) with third parties!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 6: Budget & Services */}
              {currentStep === 6 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <CreditCard className="w-12 h-12 text-[#0000fe] mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Budget & Services</h2>
                    <p className="text-white">Configure your campaign package</p>
                  </div>

                  <div className="space-y-6">
                    {/* Campaign Management - hide when custom fee is set */}
                    {!customAgencyFee && (() => {
                      const baseFee = formData.customer_status === 'new' ? 500 : 400;
                      const discountedFee = feeDiscountEligible ? Math.round(baseFee / 2) : baseFee;
                      return (
                      <div className="bg-[#0000fe]/10 border border-[#0000fe]/20 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={formData.campaign_management}
                              onChange={(e) => handleCheckboxChange('campaign_management', e.target.checked)}
                              className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-800 rounded focus:ring-[#0000fe]"
                            />
                            <label className="text-sm font-medium text-white">
                              <Target className="w-4 h-4 inline mr-2" />
                              Campaign Management
                            </label>
                          </div>
                          <span className="text-[#0000fe] font-semibold">
                            {feeDiscountEligible ? (
                              <>
                                <span className="line-through text-gray-500 mr-2">{baseFee}€</span>
                                {discountedFee}€
                              </>
                            ) : (
                              <>{baseFee}€</>
                            )}
                          </span>
                        </div>
                        <p className="text-xs text-white ml-7 mt-1">
                          Campaign setup, optimization and management
                        </p>
                        {feeDiscountEligible && (
                          <p className="text-xs text-green-400 ml-7 mt-1">
                            50% discount applied — previous campaign spend was below 50% of scheduled budget
                          </p>
                        )}
                      </div>
                      );
                    })()}

                    {/* Custom Agency Fee - show when set via URL parameter */}
                    {customAgencyFee && (
                      <div className="bg-[#0000fe]/10 border border-[#0000fe]/20 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={true}
                              disabled={true}
                              className="w-4 h-4 text-blue-600 bg-blue-600 border-blue-600 rounded opacity-100"
                            />
                            <label className="text-sm font-medium text-white">
                              <Target className="w-4 h-4 inline mr-2" />
                              Agency Fee
                            </label>
                          </div>
                          <span className="text-[#0000fe] font-semibold">{customAgencyFee.toLocaleString('de-DE')}€</span>
                        </div>
                        <p className="text-xs text-white ml-7 mt-1">
                          Kampagnen-Management
                        </p>
                      </div>
                    )}

                    {/* Content Strategy & Feedback - only available if release date is ≥4 weeks away */}
                    {!customAgencyFee && (() => {
                      const fourWeeksInMs = 28 * 24 * 60 * 60 * 1000;
                      const releaseDate = formData.release_date ? new Date(formData.release_date) : null;
                      const now = new Date();
                      now.setHours(0, 0, 0, 0);
                      const isAtLeast4WeeksOut = releaseDate ? (releaseDate.getTime() - now.getTime()) >= fourWeeksInMs : false;

                      return (
                      <div className={`bg-[#0000fe]/10 border border-[#0000fe]/20 rounded-lg p-4 ${!isAtLeast4WeeksOut ? 'opacity-50' : ''}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={formData.content_strategy_upsell}
                              onChange={(e) => handleCheckboxChange('content_strategy_upsell', e.target.checked)}
                              disabled={!isAtLeast4WeeksOut}
                              className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-800 rounded focus:ring-[#0000fe] disabled:cursor-not-allowed"
                            />
                            <label className={`text-sm font-medium text-white ${!isAtLeast4WeeksOut ? 'cursor-not-allowed' : ''}`}>
                              <Target className="w-4 h-4 inline mr-2" />
                              Content Strategy & Feedback
                            </label>
                          </div>
                          <span className="text-[#0000fe] font-semibold">1000€</span>
                        </div>
                        <p className="text-xs text-white ml-7 mt-1">
                          {isAtLeast4WeeksOut
                            ? 'Development of creative strategy and ongoing feedback'
                            : 'Only available if the release date is at least 4 weeks in advance'
                          }
                        </p>
                      </div>
                      );
                    })()}

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        <DollarSign className="w-4 h-4 inline mr-2" />
                        Ad budget (net, in addition to service fees) *
                      </label>

                      {/* Leftover budget info from previous campaigns */}
                      {leftoverBudgetLoading && (
                        <div className="mb-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-2">
                          <Loader className="w-4 h-4 text-blue-400 animate-spin" />
                          <p className="text-xs text-blue-300">
                            Checking for leftover budget from previous campaigns...
                          </p>
                        </div>
                      )}
                      {leftoverBudget !== null && !leftoverBudgetLoading && (
                        <div className="mb-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <div className="flex items-start gap-2">
                            <DollarSign className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm text-green-300 font-medium">
                                Leftover budget from previous campaign: {leftoverBudget.toLocaleString('de-DE')}€
                              </p>
                              <p className="text-xs text-green-400/70 mt-1">
                                {leftoverBudget >= 650
                                  ? `You have enough leftover to cover the minimum 650€ — your additional ad budget can be 0€.`
                                  : `We'll fill up the remaining ${Math.max(650 - leftoverBudget, 0)}€ to reach the 650€ minimum.`
                                }
                              </p>
                              <div className="mt-3 flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (leftoverBudget >= 650) {
                                      setFormData(prev => ({ ...prev, werbebudget_netto: 0 }));
                                    } else {
                                      setFormData(prev => ({ ...prev, werbebudget_netto: Math.max(650 - leftoverBudget, 0) }));
                                    }
                                  }}
                                  className="px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
                                  style={{ backgroundColor: '#0000fe' }}
                                >
                                  {leftoverBudget >= 650 ? 'Set ad budget to 0€' : `Apply leftover (pay ${Math.max(650 - leftoverBudget, 0)}€)`}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="relative">
                        <input
                          type="text"
                          name="werbebudget_netto"
                          value={formData.werbebudget_netto}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            if (value === '' || !isNaN(Number(value))) {
                              setFormData(prev => ({ ...prev, werbebudget_netto: value === '' ? 0 : Number(value) }));
                            }
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                          onBlur={(e) => {
                            const value = Number(e.target.value);
                            // Only enforce 650€ minimum if no leftover budget covers it
                            const minBudget = (leftoverBudget !== null && leftoverBudget >= 650) ? 0 : 650;
                            if (value < minBudget) {
                              setFormData(prev => ({ ...prev, werbebudget_netto: minBudget }));
                            }
                          }}
                          className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2" 
                          style={{ '--tw-ring-color': '#0000fe' } as React.CSSProperties}
                          placeholder="650"
                          required
                        />
                        <span className="absolute right-3 top-3 text-gray-400">EUR</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Minimum ad budget: 650€ net (in addition to the service fees above)
                        {leftoverBudget !== null && leftoverBudget >= 650 && (
                          <span className="text-green-400"> — covered by your leftover budget</span>
                        )}
                      </p>
                    </div>

                    {/* Total Amount Display */}
                    <div className="bg-[#0000fe]/10 border border-[#0000fe]/20 rounded-lg p-6 mt-8">
                      {(() => {
                        const campaignManagementFee = formData.customer_status === 'new' ? 500 : 400;
                        const effectiveCampaignFee = feeDiscountEligible ? Math.round(campaignManagementFee / 2) : campaignManagementFee;
                        const netTotal = (formData.campaign_management && !customAgencyFee ? effectiveCampaignFee : 0) +
                          (formData.content_strategy_upsell && !customAgencyFee ? 1000 : 0) +
                          (customAgencyFee || 0) +
                          formData.werbebudget_netto;

                        // ─── VAT logic (German VAT law for services) ───────────────
                        // Germany:              19% German VAT
                        // EU (not DE) + VAT ID: 0% (reverse charge §13b)
                        // EU (not DE) + no VAT: 19% German VAT (B2C origin principle)
                        // Non-EU (any):         0% (tax-free export — Drittländer)
                        const EU_COUNTRIES = new Set([
                          'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic',
                          'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary',
                          'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta',
                          'Netherlands', 'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia',
                          'Spain', 'Sweden',
                        ]);

                        const isGermany = formData.land === 'Germany' || formData.land === 'Deutschland';
                        const isEU = EU_COUNTRIES.has(formData.land);
                        const hasVatId = !!(formData.vat_id && formData.vat_id.trim());

                        // Determine tax scenario
                        let taxRate = 19;
                        let taxLabel = 'VAT (19%)';
                        let taxNote: string | null = null;

                        if (isGermany) {
                          taxRate = 19;
                          taxLabel = 'VAT (19%)';
                        } else if (isEU) {
                          if (hasVatId) {
                            taxRate = 0;
                            taxLabel = 'VAT (0%)';
                            taxNote = 'Reverse Charge (§13b UStG)';
                          } else {
                            // EU B2C without VAT ID — German VAT applies
                            taxRate = 19;
                            taxLabel = 'VAT (19% DE)';
                          }
                        } else {
                          // Non-EU — tax-free export of services
                          taxRate = 0;
                          taxLabel = 'VAT (0%)';
                          taxNote = 'Tax-free export (Drittländer)';
                        }

                        const taxAmount = netTotal * (taxRate / 100);
                        const grossTotal = netTotal + taxAmount;

                        // Show hint only for EU (not DE) without VAT ID
                        const needsVatIdHint = isEU && !isGermany && !hasVatId;

                        return (
                          <>
                            <div className="space-y-2 text-sm text-white mb-4">
                              {formData.campaign_management && !customAgencyFee && (
                                <div className="flex justify-between">
                                  <span>
                                    Campaign Management
                                    {feeDiscountEligible && (
                                      <span className="text-green-400 ml-2">(50% off)</span>
                                    )}
                                  </span>
                                  <span>
                                    {feeDiscountEligible ? (
                                      <>
                                        <span className="line-through text-gray-500 mr-1">{campaignManagementFee}€</span>
                                        {effectiveCampaignFee}€
                                      </>
                                    ) : (
                                      <>{campaignManagementFee}€</>
                                    )}
                                  </span>
                                </div>
                              )}
                              {formData.content_strategy_upsell && !customAgencyFee && (
                                <div className="flex justify-between">
                                  <span>Content Strategy & Feedback</span>
                                  <span>1000€</span>
                                </div>
                              )}
                              {customAgencyFee && (
                                <div className="flex justify-between">
                                  <span>Agency Fee</span>
                                  <span>{customAgencyFee.toLocaleString('de-DE')}€</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span>Ad budget</span>
                                <span>{formData.werbebudget_netto.toLocaleString('de-DE')}€</span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between pt-4 border-t border-[#0000fe]/20">
                              <span className="text-xl font-bold text-white">Total amount (net)</span>
                              <span className="text-2xl font-bold text-green-400">
                                {netTotal.toLocaleString('de-DE')}€
                              </span>
                            </div>
                            <div className="flex items-center justify-between pt-3 text-sm text-white">
                              <span>{taxLabel} {taxNote && <span className="text-gray-400">({taxNote})</span>}</span>
                              <span>{taxAmount.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€</span>
                            </div>
                            <div className="flex items-center justify-between pt-3 border-t border-[#0000fe]/20">
                              <span className="text-lg font-bold text-white">Total amount (gross)</span>
                              <span className="text-xl font-bold text-green-400">
                                {grossTotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                              </span>
                            </div>
                            {needsVatIdHint && (
                              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                                <p className="text-xs text-yellow-300">
                                  ⚠️ You selected an EU country but did not provide a VAT ID. 
                                  Without a VAT ID, 19% German VAT will be charged. 
                                  To benefit from reverse charge (0% VAT), please enter your VAT ID above.
                                </p>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6 mt-8">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id="final_confirmation"
                          checked={formData.final_confirmation}
                          onChange={(e) => {
                            handleCheckboxChange('final_confirmation', e.target.checked);
                            if (e.target.checked) {
                              setShowConfirmationWarning(false);
                            }
                          }}
                          className="w-5 h-5 text-blue-600 bg-gray-900 border-gray-800 rounded focus:ring-[#0000fe] mt-1"
                        />
                        <label htmlFor="final_confirmation" className="text-sm text-white font-medium">
                          <strong>Book campaign bindingly</strong>
                          <p className="text-white mt-2 font-normal">
                            I hereby confirm the binding booking of my campaign with the provided data.
                            I am aware that after booking, changes are only possible to a limited extent.
                          </p>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-800">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="text-white px-8 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors hover:opacity-90"
                    style={{ backgroundColor: '#0000fe' }}
                  >
                    Back
                  </button>
                )}
                
                {/* The two buttons MUST keep distinct keys. They sit at the same
                    position in the JSX tree, so without keys React reuses the same DOM
                    node and only patches its attributes — turning the just-clicked
                    "Next" button into type="submit" mid-click, which made the browser
                    submit the form as that same click's default action (booking the
                    campaign before the budget step could be used). Both buttons are
                    type="button" with explicit handlers for the same reason: never rely
                    on the native form-submit default action here. */}
                <div className="ml-auto">
                  {currentStep < totalSteps && !(currentStep === 1.7 && (formData.is_phone_verified || associatedArtists.length > 0 || isLoadingArtists)) ? (
                    <button
                      key="next-step-button"
                      type="button"
                      onClick={nextStep}
                      className="px-6 py-3 text-white rounded-lg transition-colors font-medium hover:opacity-90"
                      style={{ backgroundColor: '#0000fe' }}
                    >
                      Next
                    </button>
                  ) : currentStep >= totalSteps ? (
                    <button
                      key="book-campaign-button"
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                        isSubmitting
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'text-white hover:opacity-90'
                      }`}
                      style={{
                        backgroundColor: isSubmitting ? undefined : '#0000fe'
                      }}
                    >
                      {isSubmitting ? 'Wird gesendet...' : 'Kampagne verbindlich buchen'}
                    </button>
                  ) : null}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Submission processing spinner modal */}
      {isSubmitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 max-w-sm w-full text-center">
            <Loader className="w-12 h-12 text-[#0000fe] animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Submitting campaign</h3>
            <p className="text-sm text-gray-400">
              Uploading files and processing your booking…
            </p>
          </div>
        </div>
      )}

      {/* Step 2 WhatsApp verification spinner modal */}
      {step2Verifying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 max-w-sm w-full text-center">
            <Loader className="w-12 h-12 text-[#0000fe] animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">
              {step2CodeSent ? 'Verifying code' : 'Sending verification code'}
            </h3>
            <p className="text-sm text-gray-400">
              {step2CodeSent
                ? 'Creating your account and verifying…'
                : `Sending a 6-digit code via WhatsApp to ${formData.telefon}…`}
            </p>
          </div>
        </div>
      )}

      {/* Monday.com Import Modal */}
      {showMondayImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Download className="w-5 h-5 text-[#0000fe]" />
                Aus Monday.com importieren
              </h3>
              <button
                type="button"
                onClick={() => setShowMondayImport(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={mondaySearch}
                  onChange={(e) => {
                    setMondaySearch(e.target.value);
                    fetchMondayItems(e.target.value);
                  }}
                  placeholder="Künstler oder Release suchen..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0000fe]"
                />
              </div>
            </div>

            {/* Items list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {mondayLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader className="w-8 h-8 text-[#0000fe] animate-spin mb-3" />
                  <p className="text-gray-400 text-sm">Lade Monday.com Einträge...</p>
                </div>
              ) : mondayItems.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-sm">Keine Einträge gefunden</p>
                </div>
              ) : (
                <>
                  {mondayItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => selectMondayItem(item)}
                      className={`w-full text-left p-3 bg-gray-800 border rounded-lg transition-colors ${
                        item.already_imported
                          ? 'border-green-600/30 hover:border-green-600'
                          : 'border-gray-700 hover:border-[#0000fe]'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-white truncate">{item.name}</h4>
                            {item.release_name && (
                              <span className="text-sm text-gray-400 truncate">— {item.release_name}</span>
                            )}
                            {item.already_imported && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-600/20 text-green-400 border border-green-600/30 shrink-0">
                                ✓ Importiert
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                            {item.release_date && <span>{item.release_date}</span>}
                            {item.budget && (
                              <span className={item.budget_mismatch ? 'text-yellow-400 font-medium' : ''}>
                                Budget: {item.budget}€
                                {item.budget_mismatch && item.existing_budget != null && (
                                  <span className="text-yellow-400"> (Feedbacktool: {item.existing_budget}€)</span>
                                )}
                              </span>
                            )}
                            {item.first_name && <span>{item.first_name} {item.last_name}</span>}
                            {item.email && <span className="truncate">{item.email}</span>}
                            {item.creative_support === 'Ja' && <span className="text-[#0000fe]">Creative Support</span>}
                            {item.budget_mismatch && (
                              <span className="text-yellow-400 font-medium flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Budget abweichend
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-gray-600 shrink-0">{item.created_at?.slice(0, 10)}</span>
                      </div>
                    </button>
                  ))}

                  {/* Load more button */}
                  {mondayHasMore && (
                    <button
                      type="button"
                      onClick={loadMoreMondayItems}
                      disabled={mondayLoadingMore}
                      className="w-full py-3 text-sm text-gray-400 hover:text-white border border-gray-700 hover:border-[#0000fe] rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {mondayLoadingMore ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Lade mehr...
                        </>
                      ) : (
                        'Mehr laden'
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewCampaignSubmission;
